<template>
  <Transition name="modal-fade" appear>
    <div
      v-show="visible"
      @click.self="closeModal"
      class="fixed inset-0 z-50 transition-all duration-300 bg-opacity-50"
      :class="
        isHomePage
          ? 'bg-transparent'
          : 'bg-black/40 backdrop-blur-sm dark:bg-black/70'
      "
    >
      <div
        class="fixed inset-0 flex items-end justify-center p-6 mb-10 sm:inset-auto sm:absolute sm:bottom-0 sm:right-0 sm:mx-20 sm:my-10 sm:block"
      >
        <ul class="flex flex-col gap-4">
          <TransitionGroup name="list-stagger" tag="template">
            <li
              v-for="(item, index) in servicesLinks"
              :key="`${item.label}-${index}`"
              class="transition-transform duration-500 ease-out"
              :style="{ transitionDelay: `${index * 100}ms` }"
            >
              <!-- Active link -->
              <RouterLink
                v-if="item.active"
                :to="item.to"
                @click="closeModal"
                class="text-5xl uppercase transition-all duration-500 font-inter hover:text-6xl hover:tracking-wider"
                :class="[
                  isCurrentRoute(item.to)
                    ? isHomePage
                      ? 'text-gray-600 hover:text-gray-600 font-bold text-6xl tracking-wide'
                      : 'text-white hover:text-white/70 dark:text-white dark:hover:text-white/50 font-bold text-6xl tracking-wide'
                    : isHomePage
                      ? 'hover:text-gray-600'
                      : 'text-white hover:text-white/70 dark:text-white/80 dark:hover:text-white/60',
                ]"
              >
                {{
                  typeof item.label === "string" &&
                  item.label.startsWith("menuKeys.")
                    ? $t(item.label)
                    : item.label
                }}
              </RouterLink>

              <!-- Inactive (disabled) link -->
              <span
                v-else
                class="text-5xl uppercase transition-colors duration-300 cursor-not-allowed font-inter"
                :class="
                  isHomePage
                    ? 'text-gray-400 opacity-50'
                    : 'text-white/40 dark:text-white/30'
                "
              >
                {{
                  typeof item.label === "string" &&
                  item.label.startsWith("menuKeys.")
                    ? $t(item.label)
                    : item.label
                }}
              </span>
            </li>
          </TransitionGroup>
        </ul>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { watch, onMounted, onBeforeUnmount, computed } from "vue";
import { useRoute } from "vue-router";
import { useFooterLinks } from "~/composables/useFooterLinks";

const props = defineProps<{
  visible: boolean;
  isHomePage: boolean;
}>();

const emit = defineEmits<{
  (e: "close", value: boolean): void;
}>();

const closeModal = () => {
  emit("close", false);
};

// Get links from Supabase via composable
const { footerLinks } = useFooterLinks();

// Extract "Links" section from footer data
const servicesLinks = computed(() => {
  if (!footerLinks.value || footerLinks.value.length === 0) return [];
  const linksSection = footerLinks.value.find(
    (section: any) => section.title === "Links",
  );
  return linksSection?.content || [];
});

// Watch the visible prop to handle overflow behavior
watch(
  () => props.visible,
  (newVal) => {
    document.body.style.overflow = newVal ? "hidden" : "";
  },
);

const route = useRoute();

const isCurrentRoute = (path: string) => {
  return route.path === path;
};

// Re-enable overflow on beforeUnmount
onBeforeUnmount(() => {
  document.body.style.overflow = "";
});
</script>

<style scoped>
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}
.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.list-stagger-enter-active {
  transition: all 0.4s ease;
}
.list-stagger-enter-from {
  transform: translateY(20px);
  opacity: 0;
}
.list-stagger-leave-active {
  transition: all 0.2s ease;
}
.list-stagger-leave-to {
  transform: translateY(10px);
  opacity: 0;
}
</style>
